/**
 * eSanza Super Admin — Typed API Service Layer
 * All calls go through apiGet/apiPost/apiPatch/apiDelete from utils/api.ts.
 * No raw fetch() calls in visual components.
 * AbortSignal supported on read operations to cancel stale requests.
 */

import { apiGet, apiPost, apiPatch, apiDelete } from '../../../utils/api';
import type {
  PlatformOverview,
  TenantRow,
  TenantDetail,
  TenantFeature,
  TenantPage,
  TenantUserRow,
  RegistrationRequest,
  UserRow,
  UserDetail,
  AuditLog,
  RoleDefinition,
  HealthStatus,
} from '../types';

// ─── Helper ───────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, any>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value) !== '') {
      search.set(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// ─── Health ───────────────────────────────────────────────────────────────────

export async function getHealthStatus(signal?: AbortSignal): Promise<HealthStatus> {
  return apiGet<HealthStatus>('/health', undefined, { signal });
}

// ─── Platform Overview ────────────────────────────────────────────────────────

export async function getPlatformOverview(signal?: AbortSignal): Promise<PlatformOverview> {
  return apiGet<PlatformOverview>('/api/super-admin/overview', undefined, { signal });
}

// ─── Registration Requests ───────────────────────────────────────────────────

export async function listRegistrationRequests(signal?: AbortSignal): Promise<{ requests: RegistrationRequest[] }> {
  return apiGet<{ requests: RegistrationRequest[] }>('/api/super-admin/tenant-registration-requests', undefined, { signal });
}

export async function createInvite(body: {
  company_name: string;
  contact_name?: string;
  contact_email: string;
  contact_phone?: string;
  notes?: string;
  requested_modules?: string[];
}): Promise<{ link: string; request: RegistrationRequest }> {
  return apiPost<{ link: string; request: RegistrationRequest }>(
    '/api/super-admin/tenant-registration-requests/invite',
    body
  );
}

export async function approveRequest(id: number): Promise<{ success: boolean; request?: RegistrationRequest; tenant?: TenantRow; manager?: { id: number; email: string }; email_sent?: boolean }> {
  return apiPost<{
    success: boolean;
    request?: RegistrationRequest;
    tenant?: TenantRow;
    manager?: { id: number; email: string };
    email_sent?: boolean;
  }>(
    `/api/super-admin/tenant-registration-requests/${id}/approve`,
    {}
  );
}

export async function rejectRequest(id: number, reason?: string): Promise<{ success: boolean; request?: RegistrationRequest }> {
  return apiPost<{ success: boolean; request?: RegistrationRequest }>(
    `/api/super-admin/tenant-registration-requests/${id}/reject`,
    { reason: reason || null }
  );
}

export async function deleteRegistrationRequest(id: number): Promise<{ success: boolean }> {
  return apiDelete<{ success: boolean }>(
    `/api/super-admin/tenant-registration-requests/${id}`
  );
}

export interface PrecheckResult {
  slug: string;
  warnings: Array<{
    type: 'slug_conflict' | 'existing_user';
    message: string;
    existing_tenant?: { id: number; name: string; slug: string; status: string };
    existing_user?: { id: number; full_name: string; role: string; is_active: boolean; membership_count: number };
  }>;
}

export async function precheckRequest(id: number, signal?: AbortSignal): Promise<PrecheckResult> {
  return apiGet<PrecheckResult>(
    `/api/super-admin/tenant-registration-requests/${id}/precheck`,
    undefined,
    { signal }
  );
}

// ─── Tenants / Organisations ─────────────────────────────────────────────────

export interface ListTenantsParams {
  search?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export async function listTenants(
  params: ListTenantsParams = {},
  signal?: AbortSignal
): Promise<{ tenants: TenantRow[]; total: number; page: number; limit: number }> {
  return apiGet<{ tenants: TenantRow[]; total: number; page: number; limit: number }>(
    `/api/super-admin/tenants${buildQuery(params)}`,
    undefined,
    { signal }
  );
}

export async function getTenantDetail(id: number, signal?: AbortSignal): Promise<{ tenant: TenantDetail }> {
  return apiGet<{ tenant: TenantDetail }>(`/api/super-admin/tenants/${id}`, undefined, { signal });
}

export async function getTenantUsers(id: number, signal?: AbortSignal): Promise<{ users: TenantUserRow[]; total: number }> {
  return apiGet<{ users: TenantUserRow[]; total: number }>(`/api/super-admin/tenants/${id}/users`, undefined, { signal });
}

export async function createTenant(body: {
  name: string;
  slug?: string;
  legal_name?: string;
  domain?: string;
  admin_domain?: string;
  contact_email?: string;
  contact_phone?: string;
  country?: string;
  marketplace_enabled?: boolean;
}): Promise<{ tenant: TenantDetail }> {
  return apiPost<{ tenant: TenantDetail }>('/api/super-admin/tenants', body);
}

export async function updateTenant(
  id: number,
  body: Partial<{
    name: string;
    legal_name: string;
    domain: string;
    admin_domain: string;
    contact_email: string;
    contact_phone: string;
    country: string;
    status: string;
    marketplace_enabled: boolean;
    settings: Record<string, any>;
  }>
): Promise<{ tenant: TenantDetail }> {
  return apiPatch<{ tenant: TenantDetail }>(`/api/super-admin/tenants/${id}`, body);
}

export async function deleteTenant(
  id: number,
  permanent = false
): Promise<{ mode: 'archived' | 'deleted'; tenant?: TenantRow; linked?: Record<string, number> }> {
  return apiDelete<{ mode: 'archived' | 'deleted'; tenant?: TenantRow; linked?: Record<string, number> }>(
    `/api/super-admin/tenants/${id}${permanent ? '?permanent=true' : ''}`
  );
}

export interface TenantDependencies {
  tenant: { id: number; name: string; slug: string; status: string };
  linked: Record<string, number>;
  linkedTotal: number;
  canDeletePermanently: boolean;
  linkedLabels?: Record<string, string>;
}

export async function getTenantDependencies(
  id: number,
  signal?: AbortSignal
): Promise<TenantDependencies> {
  return apiGet<TenantDependencies>(
    `/api/super-admin/tenants/${id}/dependencies`,
    undefined,
    { signal }
  );
}

export async function restoreTenant(
  id: number
): Promise<{ success: boolean; tenant: TenantDetail }> {
  return apiPost<{ success: boolean; tenant: TenantDetail }>(
    `/api/super-admin/tenants/${id}/restore`,
    {}
  );
}

export async function updateTenantFeature(
  tenantId: number,
  featureKey: string,
  isEnabled: boolean
): Promise<{ feature: TenantFeature }> {
  return apiPatch<{ feature: TenantFeature }>(
    `/api/super-admin/tenants/${tenantId}/features/${featureKey}`,
    { is_enabled: isEnabled }
  );
}

export async function updateTenantPage(
  tenantId: number,
  pageKey: string,
  isEnabled: boolean
): Promise<{ page: TenantPage }> {
  return apiPatch<{ page: TenantPage }>(
    `/api/super-admin/tenants/${tenantId}/pages/${pageKey}`,
    { is_enabled: isEnabled }
  );
}

// ─── Platform Users ───────────────────────────────────────────────────────────

export interface ListUsersParams {
  search?: string;
  role?: string;
  status?: string;
  tenant_id?: number | string;
  page?: number;
  limit?: number;
}

export async function listUsers(
  params: ListUsersParams = {},
  signal?: AbortSignal
): Promise<{ users: UserRow[]; total: number }> {
  return apiGet<{ users: UserRow[]; total: number }>(
    `/api/super-admin/users${buildQuery(params)}`,
    undefined,
    { signal }
  );
}

export async function getUserDetail(id: number, signal?: AbortSignal): Promise<{ user: UserDetail }> {
  return apiGet<{ user: UserDetail }>(`/api/super-admin/users/${id}`, undefined, { signal });
}

export async function changeUserTenantRole(
  userId: number,
  tenantId: number,
  role: string
): Promise<{ success: boolean; membership: any }> {
  return apiPatch<{ success: boolean; membership: any }>(
    `/api/super-admin/users/${userId}/memberships/${tenantId}/role`,
    { role }
  );
}

export async function updateUserIdentity(
  userId: number,
  body: Partial<{ email: string; phone: string; full_name: string; is_active: boolean }>
): Promise<{ user: UserDetail; warning?: string }> {
  return apiPatch<{ user: UserDetail; warning?: string }>(
    `/api/super-admin/users/${userId}/identity`,
    body
  );
}

export async function suspendUser(
  userId: number,
  suspend: boolean,
  reason?: string
): Promise<{ success: boolean; message: string }> {
  return apiPatch<{ success: boolean; message: string }>(
    `/api/super-admin/users/${userId}/suspend`,
    { suspend, reason }
  );
}

export async function deleteUser(userId: number): Promise<{ success: boolean; message: string }> {
  return apiDelete<{ success: boolean; message: string }>(`/api/super-admin/users/${userId}`);
}

// ─── Audit Logs ───────────────────────────────────────────────────────────────

export interface ListAuditLogsParams {
  action?: string;
  actor_id?: number | string;
  module?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export async function listAuditLogs(
  params: ListAuditLogsParams = {},
  signal?: AbortSignal
): Promise<{ logs: AuditLog[]; total: number }> {
  return apiGet<{ logs: AuditLog[]; total: number }>(
    `/api/super-admin/audit-logs${buildQuery(params)}`,
    undefined,
    { signal }
  );
}

// ─── Roles ────────────────────────────────────────────────────────────────────

export async function getRoles(signal?: AbortSignal): Promise<{ roles: RoleDefinition[] }> {
  return apiGet<{ roles: RoleDefinition[] }>('/api/super-admin/roles', undefined, { signal });
}
